import type { IGame } from './IGame';
import type { Command, GameState } from './definitions';
import { GameAction, GameEvent, RoundStage } from './definitions';
import { action, getValidActions } from './engine';
import { EventEmitter } from './eventEmitter';
import type { ActionData, CardString } from './general_definitions';
import { Rules } from './rules';

import {
	type AgentBase,
	getCardString,
	getPlayerHandName,
	makePassCommand,
	makePlayCardCommand,
	makeToggleCardCommand,
} from '../';

export class Manager extends EventEmitter implements IGame {
	gameState!: GameState;
	users: AgentBase[] = [];

	constructor(users: AgentBase[] = [], gameState?: GameState) {
		super();
		this.users = users;

		if(gameState){
			this.loadGameState(gameState);
		}

	}

	loadGameState(gameState?: GameState) {
		return this.action(GameAction.LoadGame, 0, this.users, gameState);
	}

	createNewGame() {
		return this.action(GameAction.NewGame, 0, this.users);
	}

	nextStage() {
		return this.action(GameAction.NextStage);
	}

	handleFinishPass(localPlayer: number) {
		const gameState = this.gameState;
		//Get cards that the local player marked for passing
		const playerHand = getPlayerHandName(localPlayer);

		const cards = gameState.deck.filter((c) => c.isSelected && c.pile === playerHand);
		const action = makePassCommand(localPlayer, cards.map(getCardString));
		return this.action(action);

	}

	handleCardSelection(card: CardString, localPlayer: number) {
		const command = this.gameState.roundStage === RoundStage.Pass ? makeToggleCardCommand(localPlayer, card) : makePlayCardCommand(localPlayer, card);

		return this.action(command);
	}

	can(command: GameAction | Command, gameState?: GameState, position?: number, data?: ActionData): boolean {
		return Rules.can(command, gameState ?? this.gameState, position, data);
	}

	action(command: GameAction | Command, position?: number | undefined, data?: ActionData | undefined, gameState?: GameState): boolean {
		gameState = gameState ?? this.gameState;

		if (!this.can(command, gameState, position, data)) {
			return false;
		}

		this.gameState = action(command, gameState, position, data);

		this.emitNewState();
		return true;
	}

	getValidActions(position: number, gameState?: GameState | undefined): Command[] {
		gameState = gameState ?? this.gameState;
		return getValidActions(gameState, position);
	}

	tick() {
		for (const user of this.users) {
			if (user) {
				user.tick(this);
			}
		}
	}

	forceNextStep() {
		const playerCount = this.gameState.playerCount;

		switch (this.gameState.roundStage) {
			case RoundStage.Deal:
				this.action(GameAction.Deal);
				break;
			case RoundStage.Pass:
				for (let x = 0; x < playerCount; ++x) {
					this.action(this.getValidActions(x)[0]);
				}

				this.action(GameAction.NextStage);
				break;
			case RoundStage.Trick:
				for (let x = 0; x < playerCount; ++x) {
					this.action(this.getValidActions(this.gameState.currentPlayer)[0]);
				}

				this.action(GameAction.NextStage);
				break;
			case RoundStage.PassEnd:
			case RoundStage.TrickEnd:
			case RoundStage.RoundEnd:
				this.action(GameAction.NextStage);
				break;

		}
	}

	protected emitNewState() {
		this.emit(GameEvent.StateChanged, this.gameState, this);
	}

}
